import React, { useEffect, useRef, useState } from 'react';
import Style from './Portfolio.module.scss';

const INTENTS = [
    {
        label: 'Check that login returns a token',
        script: [
            { t: '$ cognivox run --intent "check that login returns a token"', tone: 'cmd' },
            { t: '⟳ parsing intent → matched auth.login.token', tone: 'dim' },
            { t: '⟳ generating request…', tone: 'dim' },
            { t: 'POST /api/v1/auth/login', tone: 'req' },
            { t: '{ "email": "qa@demo.dev", "password": "••••••••" }', tone: 'txt' },
            { t: '→ executing against staging…', tone: 'dim' },
            { t: '✓ status 200 — expected 200 · 84ms', tone: 'ok' },
            { t: '✓ body.token matches ^eyJ… (JWT)', tone: 'ok' },
            { t: '✓ body.expires_in ≤ 3600', tone: 'ok' },
            { t: 'suite passed · 3 assertions · 96ms', tone: 'pass' },
        ],
    },
    {
        label: 'Verify checkout rejects an expired card',
        script: [
            { t: '$ cognivox run --intent "verify checkout rejects an expired card"', tone: 'cmd' },
            { t: '⟳ parsing intent → matched payments.checkout.validation', tone: 'dim' },
            { t: '⟳ generating request with expired card fixture…', tone: 'dim' },
            { t: 'POST /api/v1/checkout', tone: 'req' },
            { t: '{ "card": { "number": "4111…", "exp": "01/22" }, "amount": 4900 }', tone: 'txt' },
            { t: '→ executing against staging…', tone: 'dim' },
            { t: '✓ status 422 — expected 4xx · 61ms', tone: 'ok' },
            { t: '✓ error.code == "card_expired"', tone: 'ok' },
            { t: '✓ no charge created (side-effect check)', tone: 'ok' },
            { t: 'suite passed · 3 assertions · 74ms', tone: 'pass' },
        ],
    },
    {
        label: 'Ensure search paginates correctly',
        script: [
            { t: '$ cognivox run --intent "ensure search paginates correctly"', tone: 'cmd' },
            { t: '⟳ parsing intent → matched search.list.pagination', tone: 'dim' },
            { t: '⟳ generating request chain (2 dependent calls)…', tone: 'dim' },
            { t: 'GET /api/v1/search?q=widgets&page=1&limit=10', tone: 'req' },
            { t: 'GET /api/v1/search?q=widgets&page=2&limit=10', tone: 'req' },
            { t: '→ executing DAG…', tone: 'dim' },
            { t: '✓ page 1 returns 10 items · 58ms', tone: 'ok' },
            { t: '✓ page 2 has no overlap with page 1', tone: 'ok' },
            { t: '✓ total_count consistent across pages', tone: 'ok' },
            { t: 'suite passed · 3 assertions · 121ms', tone: 'pass' },
        ],
    },
];

export default function TestRunDemo() {
    const [intent, setIntent] = useState(0);
    const [shown, setShown] = useState(0);
    const [running, setRunning] = useState(false);
    const timerRef = useRef(null);

    const script = INTENTS[intent].script;

    const run = () => {
        clearInterval(timerRef.current);
        setShown(0);
        setRunning(true);
        let count = 0;
        timerRef.current = setInterval(() => {
            count += 1;
            setShown(count);
            if (count >= script.length) {
                clearInterval(timerRef.current);
                setRunning(false);
            }
        }, 220);
    };

    const pick = (i) => {
        clearInterval(timerRef.current);
        setIntent(i);
        setShown(0);
        setRunning(false);
    };

    useEffect(() => () => clearInterval(timerRef.current), []);

    return (
        <div className={Style.demo}>
            <div className={Style.demoIntents}>
                {INTENTS.map((it, i) => (
                    <button
                        key={i}
                        className={i === intent ? Style.demoIntentActive : Style.demoIntent}
                        onClick={() => pick(i)}
                    >
                        “{it.label}”
                    </button>
                ))}
            </div>

            <div className={Style.demoConsole}>
                {shown === 0 && !running && (
                    <p className={Style.demoHint}>
                        pick an intent in plain English, then run it ↓
                    </p>
                )}
                {script.slice(0, shown).map((line, i) => (
                    <p key={i} className={Style[`demo_${line.tone}`]}>{line.t}</p>
                ))}
            </div>

            <div className={Style.demoFoot}>
                <button className={Style.demoRun} onClick={run} disabled={running}>
                    {running ? '⟳ running…' : shown >= script.length ? '↺ run again' : '▶ run test'}
                </button>
                <span className={Style.demoNote}>
                    simulated — the real engine runs at cognivox-ai.com
                </span>
            </div>
        </div>
    );
}
